/**
 * v1.1 AI 管理后台 /admin/ai-insights
 *
 * 4 tab：
 *   - 漏斗诊断（AiFunnelTab）
 *   - 员工高风险（AiEmployeeTab）
 *   - 反馈统计（AiFeedbackTab）
 *   - 调用统计（AiUsageTab）
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tabs, message } from 'antd';
import { $ } from '../lib/api';
import { fmtMonthDayTime } from '../lib/format';
import { PageHeader, HomeCard, Btn, Pill, TableWrap, Avatar } from '../components/ui';

export function AiInsightsAdminPage() {
  return (
    <>
      <PageHeader breadcrumb={['Admin', 'AI 洞察']} title="🧠 AI 洞察管理"
                  subtitle="漏斗诊断 / 员工分析 / 反馈 / 调用统计" />
      <div className="work-content">
        <Tabs items={[
          { key: 'funnel', label: '漏斗诊断', children: <AiFunnelTab /> },
          { key: 'employee', label: '员工分析', children: <AiEmployeeTab /> },
          { key: 'feedback', label: '反馈统计', children: <AiFeedbackTab /> },
          { key: 'usage', label: '调用统计', children: <AiUsageTab /> },
        ]} />
      </div>
    </>
  );
}

function AiFunnelTab() {
  const qc = useQueryClient();
  const { data } = useQuery<any[]>({
    queryKey: ['ai-funnel-open'],
    queryFn: () => $.get('/ai/funnel/open'),
  });
  const diagnose = useMutation({
    mutationFn: () => $.post('/ai/funnel/diagnose', {}),
    onSuccess: (r: any) => {
      message.success(`诊断完成 · ${r.total ?? 0} 个异常`);
      qc.invalidateQueries({ queryKey: ['ai-funnel-open'] });
    },
    onError: (e: any) => message.error(e?.message ?? '诊断失败'),
  });
  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Btn kind="primary" onClick={() => diagnose.mutate()} disabled={diagnose.isPending}>
          {diagnose.isPending ? 'AI 诊断中（10-30 秒）…' : '🧠 立即诊断漏斗'}
        </Btn>
      </div>
      {(data ?? []).length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
          暂无开放诊断 — 点上方按钮立即跑
        </div>
      )}
      {(data ?? []).map((d: any) => (
        <HomeCard key={d.id} title={`🧠 ${d.anomalyStageFrom} → ${d.anomalyStageTo}`}>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <Pill tone={Number(d.deviationPct) < 0 ? 'danger' : 'success'}>
                偏离 {Number(d.deviationPct).toFixed(0)}%
              </Pill>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                历史 {(Number(d.historicalRate) * 100).toFixed(1)}% → 当前 {(Number(d.currentRate) * 100).toFixed(1)}%
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
                {fmtMonthDayTime(d.diagnosedAt)}
              </span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>📊 异常摘要：</strong>{d.diagnosisJson?.anomaly_summary}
            </div>
            {Array.isArray(d.diagnosisJson?.common_issues) && (
              <div style={{ marginBottom: 12 }}>
                <strong>📊 共性问题：</strong>
                <ul style={{ paddingLeft: 18, margin: '4px 0 0' }}>
                  {d.diagnosisJson.common_issues.map((it: string, i: number) => <li key={i}>{it}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(d.diagnosisJson?.root_causes) && (
              <div style={{ marginBottom: 12 }}>
                <strong>🎯 根因：</strong>
                <ul style={{ paddingLeft: 18, margin: '4px 0 0' }}>
                  {d.diagnosisJson.root_causes.map((it: string, i: number) => <li key={i}>{it}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(d.diagnosisJson?.recommendations) && (
              <div>
                <strong>💡 改进建议：</strong>
                {d.diagnosisJson.recommendations.map((r: any, i: number) => (
                  <div key={i} style={{
                    padding: 8, marginTop: 6,
                    background: 'rgba(51,112,255,0.06)', borderRadius: 4,
                  }}>
                    <div><strong>{i + 1}.</strong> {typeof r === 'string' ? r : r.action}
                      {r.priority && <Pill tone={r.priority === 'high' ? 'danger' : 'warning'}>{r.priority}</Pill>}
                    </div>
                    {r.expected_recovery && (
                      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>
                        预期恢复：{r.expected_recovery}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </HomeCard>
      ))}
    </>
  );
}

function AiEmployeeTab() {
  const qc = useQueryClient();
  const { data: rows } = useQuery<any[]>({
    queryKey: ['ai-emp-high-risk'],
    queryFn: () => $.get('/ai/employees/high-risk'),
  });
  return (
    <HomeCard title={`🚨 高离职风险员工（${rows?.length ?? 0}）`}>
      <TableWrap>
        <table className="ck-table">
          <thead>
            <tr>
              <th>员工</th><th>风险</th><th>能力画像</th>
              <th>建议</th><th>分析时间</th><th></th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).length === 0 && (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                暂无高风险员工
              </td></tr>
            )}
            {(rows ?? []).map((r: any) => (
              <tr key={r.id}>
                <td><strong>#{r.userId}</strong></td>
                <td>
                  <Pill tone="danger">{r.leavingRiskScore}%</Pill>
                </td>
                <td style={{ fontSize: 12 }}>
                  {r.capabilityProfile && typeof r.capabilityProfile === 'object'
                    ? Object.entries(r.capabilityProfile).map(([k, v]) =>
                        `${k}:${v}`).join(' / ') : '—'}
                </td>
                <td style={{ fontSize: 12, maxWidth: 280, whiteSpace: 'normal' }}>
                  {Array.isArray(r.analysisJson?.leaving_risk?.intervention_suggestions)
                    ? r.analysisJson.leaving_risk.intervention_suggestions.slice(0, 2).join('；')
                    : (r.analysisJson?.growth_recommendations?.[0]?.action ?? '—')}
                </td>
                <td>{fmtMonthDayTime(r.analyzedAt)}</td>
                <td><Btn kind="default" onClick={() => message.info('详情页 v1.2 上线')}>详情</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </HomeCard>
  );
}

function AiFeedbackTab() {
  const { data: stats } = useQuery<any[]>({
    queryKey: ['ai-fb-stats'],
    queryFn: () => $.get('/ai/feedback/stats'),
  });
  const { data: recent } = useQuery<any[]>({
    queryKey: ['ai-fb-recent'],
    queryFn: () => $.get('/ai/feedback/recent'),
  });
  return (
    <>
      <HomeCard title="按模块反馈统计">
        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr><th>模块</th><th>总计</th><th>👍 准确</th><th>🤔 一般</th><th>👎 不准</th><th>准确率</th></tr>
            </thead>
            <tbody>
              {(stats ?? []).map((s: any) => {
                const rate = s.total > 0 ? Math.round((s.good / s.total) * 100) : 0;
                return (
                  <tr key={s.module}>
                    <td><strong>{s.module}</strong></td>
                    <td>{s.total}</td>
                    <td>{s.good}</td>
                    <td>{s.mid}</td>
                    <td>{s.bad}</td>
                    <td>
                      <Pill tone={rate >= 70 ? 'success' : rate >= 50 ? 'warning' : 'danger'}>
                        {rate}%
                      </Pill>
                    </td>
                  </tr>
                );
              })}
              {(stats ?? []).length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无反馈</td></tr>
              )}
            </tbody>
          </table>
        </TableWrap>
      </HomeCard>
      <div style={{ marginTop: 16 }}>
        <HomeCard title="最近用户评论">
          {(recent ?? []).map((c: any) => (
            <div key={c.id} style={{ padding: 10, borderBottom: '1px solid var(--border-default)' }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                {c.module} · {fmtMonthDayTime(c.createdAt)} · 评分 {c.rating}
              </div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{c.comment}</div>
            </div>
          ))}
          {(recent ?? []).length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无评论</div>
          )}
        </HomeCard>
      </div>
    </>
  );
}

function AiUsageTab() {
  const { data } = useQuery<any[]>({
    queryKey: ['ai-usage-month'],
    queryFn: () => $.get('/ai/usage/month'),
  });
  const totalCost = (data ?? []).reduce((s: number, r: any) => s + Number(r.cost ?? 0), 0);
  const totalCalls = (data ?? []).reduce((s: number, r: any) => s + Number(r.calls ?? 0), 0);
  return (
    <HomeCard title={`本月 AI 调用 · ${totalCalls} 次 · ¥${totalCost.toFixed(2)}`}>
      <TableWrap>
        <table className="ck-table">
          <thead>
            <tr>
              <th>Feature</th><th>调用</th><th>Prompt</th><th>Completion</th>
              <th>成本</th><th>成功率</th><th>平均延迟</th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((r: any) => {
              const okRate = r.calls > 0 ? Math.round((r.ok / r.calls) * 100) : 100;
              return (
                <tr key={r.feature_key}>
                  <td><strong>{r.feature_key}</strong></td>
                  <td>{r.calls}</td>
                  <td>{Number(r.pt ?? 0).toLocaleString()}</td>
                  <td>{Number(r.ct ?? 0).toLocaleString()}</td>
                  <td>¥{Number(r.cost ?? 0).toFixed(4)}</td>
                  <td>
                    <Pill tone={okRate >= 95 ? 'success' : okRate >= 85 ? 'warning' : 'danger'}>{okRate}%</Pill>
                  </td>
                  <td>{Math.round(Number(r.avg_latency ?? 0))}ms</td>
                </tr>
              );
            })}
            {(data ?? []).length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>本月无调用</td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>
    </HomeCard>
  );
}
